/**
 * Capture Review — Mimir Object Confirmation Queue
 * Route: /captures
 *
 * Review pending Mimir captures: confirm, discard, or update-then-confirm.
 * Flags refusal-shaped extractions to prevent confirming garbage.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Badge, Button, Spinner, TextInput } from 'flowbite-react';
import {
  HiCheck,
  HiTrash,
  HiRefresh,
  HiExclamation,
  HiChevronDown,
  HiChevronUp,
  HiExclamationCircle,
  HiPencil,
  HiClock,
  HiReply,
  HiArchive,
} from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { capturesApi } from '../api/captures';
import type { Capture, CaptureActionPayload } from '../api/captures';

const CaptureReview: React.FC = () => {
  const { user } = useStatus();

  const [captures, setCaptures] = useState<Capture[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActioning, setIsActioning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [statusFilter, setStatusFilter] = useState('pending');

  const [focusIndex, setFocusIndex] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [viewMode, setViewMode] = useState<'flat' | 'domain'>('flat');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => { loadCaptures(); }, [statusFilter]);

  const loadCaptures = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await capturesApi.list(statusFilter);
      if (response.success && response.data) {
        const data = (response.data as any).data || response.data;
        setCaptures(data.records || []);
        setStatusCounts(data.status_counts || {});
        setFocusIndex(0);
      } else {
        setError(response.error || 'Failed to load captures');
      }
    } catch {
      setError('Failed to load captures');
    } finally {
      setIsLoading(false);
    }
  };

  const showToast = useCallback((text: string, type: 'success' | 'error' = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const removeCard = useCallback((id: string) => {
    setCaptures(prev => prev.filter(c => c.document_id !== id));
    setEditingId(null);
  }, []);

  const doAction = useCallback(async (capture: Capture, action: CaptureActionPayload['action'], edits?: CaptureActionPayload['edits']) => {
    setIsActioning(capture.document_id);
    try {
      const payload: CaptureActionPayload = {
        object_id: capture.document_id,
        action,
        edits,
      };
      const response = await capturesApi.action(payload);
      if (response.success) {
        removeCard(capture.document_id);
        const verbs: Record<string, string> = { confirm: 'Confirmed', discard: 'Discarded', restore: 'Restored', archive: 'Archived' };
        showToast(`${verbs[action]}: ${capture.name}`);
        loadCaptures();
      } else {
        showToast(response.error || `Failed to ${action}`, 'error');
      }
    } catch {
      showToast(`Failed to ${action}`, 'error');
    } finally {
      setIsActioning(null);
    }
  }, [removeCard, showToast]);

  const startEdit = (capture: Capture) => {
    setEditingId(capture.document_id);
    setEditName(capture.name);
    setEditDomain(capture.domain || '');
  };

  const confirmWithEdits = (capture: Capture) => {
    const edits: CaptureActionPayload['edits'] = {};
    if (editName !== capture.name) edits.name = editName;
    if (editDomain !== (capture.domain || '')) edits.domain = editDomain;
    doAction(capture, 'confirm', Object.keys(edits).length > 0 ? edits : undefined);
  };

  // Keyboard handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (editingId) return;

      switch (e.key) {
        case 'j': {
          e.preventDefault();
          const next = Math.min(focusIndex + 1, captures.length - 1);
          setFocusIndex(next);
          cardRefs.current.get(next)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          break;
        }
        case 'k': {
          e.preventDefault();
          const prev = Math.max(focusIndex - 1, 0);
          setFocusIndex(prev);
          cardRefs.current.get(prev)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          break;
        }
        case 'a': {
          e.preventDefault();
          const cap = captures[focusIndex];
          if (cap) doAction(cap, 'confirm');
          break;
        }
        case 'r': {
          e.preventDefault();
          const cap = captures[focusIndex];
          if (cap) doAction(cap, 'discard');
          break;
        }
        case 'e': {
          e.preventDefault();
          const cap = captures[focusIndex];
          if (cap) startEdit(cap);
          break;
        }
        case 'x': {
          e.preventDefault();
          const cap = captures[focusIndex];
          if (!cap) break;
          setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(cap.document_id)) next.delete(cap.document_id);
            else next.add(cap.document_id);
            return next;
          });
          break;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusIndex, captures, editingId, doAction]);

  useEffect(() => {
    if (focusIndex >= captures.length && captures.length > 0) {
      setFocusIndex(captures.length - 1);
    }
  }, [captures.length, focusIndex]);

  const domainGroups = React.useMemo(() => {
    if (viewMode !== 'domain') return [];
    const groupMap = new Map<string, Capture[]>();
    for (const c of captures) {
      const key = c.domain || 'unclassified';
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key)!.push(c);
    }
    return Array.from(groupMap.entries())
      .map(([key, items]) => ({ domain: key, items }))
      .sort((a, b) => b.items.length - a.items.length);
  }, [captures, viewMode]);

  if (isLoading && captures.length === 0) {
    return <div className="flex justify-center items-center min-h-96"><Spinner size="xl" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Capture Review</h1>
          <Badge color="info">{statusCounts.pending || 0} pending</Badge>
          <div className="flex ml-2">
            <button
              onClick={() => setViewMode('flat')}
              className={`px-2 py-1.5 text-xs rounded-l border ${
                viewMode === 'flat'
                  ? 'bg-gray-100 text-gray-800 border-gray-300 font-medium'
                  : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Flat
            </button>
            <button
              onClick={() => { setViewMode('domain'); setExpandedGroups(new Set()); }}
              className={`px-2 py-1.5 text-xs rounded-r border-t border-r border-b ${
                viewMode === 'domain'
                  ? 'bg-gray-100 text-gray-800 border-gray-300 font-medium'
                  : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
              }`}
            >
              By Domain
            </button>
          </div>
        </div>
        <Button size="xs" color="light" onClick={loadCaptures} disabled={isLoading}>
          <HiRefresh className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Status tabs */}
      <div className="flex items-center gap-2 text-sm">
        {['pending', 'confirmed', 'discarded', 'archived', 'all'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-2 py-1 rounded text-xs font-medium ${
              statusFilter === s ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {s} {statusCounts[s] !== undefined ? `(${statusCounts[s]})` : ''}
          </button>
        ))}
      </div>

      {/* Toast + error */}
      {toast && (
        <div className={`p-3 rounded-md flex items-center gap-2 text-sm ${
          toast.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {toast.type === 'success' ? <HiCheck className="h-4 w-4" /> : <HiExclamation className="h-4 w-4" />}
          {toast.text}
        </div>
      )}
      {error && (
        <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm flex items-center gap-2">
          <HiExclamation className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Keyboard legend */}
      <div className="text-xs text-gray-400 flex items-center gap-3 flex-wrap">
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">j</kbd>/<kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">k</kbd> navigate</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">a</kbd> confirm</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">r</kbd> discard</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">e</kbd> edit</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">x</kbd> expand</span>
      </div>

      {/* Cards */}
      {captures.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <HiCheck className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">
            {statusFilter === 'pending' ? 'No pending captures.' : 'No captures match the current filter.'}
          </p>
        </div>
      ) : viewMode === 'domain' ? (
        <div className="space-y-3">
          {domainGroups.map(group => {
            const isGroupExpanded = expandedGroups.has(group.domain);
            return (
              <div key={group.domain} className="border border-gray-200 rounded-lg bg-white">
                <div
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedGroups(prev => {
                    const next = new Set(prev);
                    if (next.has(group.domain)) next.delete(group.domain);
                    else next.add(group.domain);
                    return next;
                  })}
                >
                  {isGroupExpanded
                    ? <HiChevronUp className="h-4 w-4 text-gray-400" />
                    : <HiChevronDown className="h-4 w-4 text-gray-400" />
                  }
                  <Badge color="indigo" className="text-xs font-semibold">{group.domain}</Badge>
                  <span className="text-sm text-gray-600 font-medium">{group.items.length} capture{group.items.length !== 1 ? 's' : ''}</span>
                </div>
                {isGroupExpanded && (
                  <div className="border-t border-gray-100 px-3 pb-3 pt-1 space-y-2">
                    {group.items.map(capture => {
                      const isProcessing = isActioning === capture.document_id;
                      const isRefusal = capture.refusal_flag?.is_refusal;
                      return (
                        <div
                          key={capture.document_id}
                          className={`rounded border p-2 text-sm ${
                            isRefusal ? 'border-orange-200 bg-orange-50/20' : 'border-gray-100 bg-gray-50/30'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-gray-800 truncate max-w-md font-medium">{capture.name}</span>
                                <Badge color="gray" className="text-xs">{capture.object_type}</Badge>
                                {isRefusal && <Badge color="failure" className="text-xs">REFUSAL</Badge>}
                              </div>
                              {capture.registered_by && (
                                <span className="text-xs text-gray-400">by {capture.registered_by}</span>
                              )}
                            </div>
                            {statusFilter === 'pending' && (
                              <div className="flex gap-1 flex-shrink-0">
                                <button
                                  onClick={() => doAction(capture, 'confirm')}
                                  disabled={isProcessing}
                                  className="p-1 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
                                  title="Confirm"
                                >
                                  <HiCheck className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => doAction(capture, 'discard')}
                                  disabled={isProcessing}
                                  className="p-1 rounded hover:bg-red-100 text-red-600 disabled:opacity-50"
                                  title="Discard"
                                >
                                  <HiTrash className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {captures.map((capture, idx) => {
            const isFocused = idx === focusIndex;
            const isExpanded = expanded.has(capture.document_id);
            const isProcessing = isActioning === capture.document_id;
            const isEditing = editingId === capture.document_id;
            const isRefusal = capture.refusal_flag?.is_refusal;
            const hasEmbeddedData = capture.refusal_flag?.has_embedded_data;

            return (
              <div
                key={capture.document_id}
                ref={el => { if (el) cardRefs.current.set(idx, el); }}
                onClick={() => setFocusIndex(idx)}
                className={`rounded-lg border p-3 transition-all cursor-pointer ${
                  isRefusal
                    ? isFocused
                      ? 'ring-2 ring-orange-400 border-orange-300 bg-orange-50/30'
                      : 'border-orange-200 bg-orange-50/20'
                    : isFocused
                      ? 'ring-2 ring-blue-400 border-blue-300 bg-blue-50/30'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Refusal warning */}
                    {isRefusal && (
                      <div className={`mb-2 p-2 rounded text-xs flex items-start gap-1.5 ${
                        hasEmbeddedData
                          ? 'bg-amber-50 border border-amber-200 text-amber-800'
                          : 'bg-red-50 border border-red-200 text-red-800'
                      }`}>
                        <HiExclamationCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium">Refusal-shaped extraction</span>
                          {hasEmbeddedData ? (
                            <span> — contains real data inside refusal wrapper. Consider extracting the payload before discarding.</span>
                          ) : (
                            <span> — entity_properties contain extraction failure, not real data. Confirming would make garbage searchable.</span>
                          )}
                          <div className="mt-1 text-xs opacity-75">
                            Indicators: {capture.refusal_flag.refusal_indicators?.join(', ')}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Name + badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{capture.name}</span>
                      <Badge color="gray" className="text-xs">{capture.object_type}</Badge>
                      {capture.entity_type && <Badge color="info" className="text-xs">{capture.entity_type}</Badge>}
                      {capture.domain && <Badge color="indigo" className="text-xs">{capture.domain}</Badge>}
                      <Badge color={capture.review_status === 'pending' ? 'warning' : capture.review_status === 'confirmed' ? 'success' : 'failure'} className="text-xs">
                        {capture.review_status}
                      </Badge>
                    </div>

                    {/* Meta line */}
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
                      {capture.capture_method && <span>via {capture.capture_method}</span>}
                      {capture.registered_by && <span>by {capture.registered_by}</span>}
                      <span>{new Date(capture.created_at).toLocaleDateString()}</span>
                    </div>

                    {/* Edit form */}
                    {isEditing && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                          <TextInput
                            size={32}
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            sizing="sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Domain</label>
                          <TextInput
                            size={32}
                            value={editDomain}
                            onChange={e => setEditDomain(e.target.value)}
                            sizing="sm"
                            placeholder="e.g. patents, security"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button size="xs" color="success" onClick={() => confirmWithEdits(capture)}>
                            Confirm with edits
                          </Button>
                          <Button size="xs" color="light" onClick={() => setEditingId(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="mt-2 bg-gray-50 rounded p-3 border border-gray-100">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Entity Properties</span>
                        <pre className="text-xs text-gray-700 mt-1 whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto font-mono">
                          {capture.entity_properties
                            ? JSON.stringify(capture.entity_properties, null, 2)
                            : '(none)'}
                        </pre>
                        {capture.source_url && (
                          <div className="mt-2 text-xs">
                            <span className="text-gray-400">Source:</span>{' '}
                            <span className="text-gray-600 break-all">{capture.source_url}</span>
                          </div>
                        )}
                        {capture.tags && capture.tags.length > 0 && (
                          <div className="mt-1 text-xs">
                            <span className="text-gray-400">Tags:</span>{' '}
                            <span className="text-gray-600">{capture.tags.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expand toggle */}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setExpanded(prev => {
                        const next = new Set(prev);
                        if (next.has(capture.document_id)) next.delete(capture.document_id);
                        else next.add(capture.document_id);
                        return next;
                      });
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    tabIndex={-1}
                  >
                    {isExpanded ? <HiChevronUp className="h-4 w-4" /> : <HiChevronDown className="h-4 w-4" />}
                  </button>

                  {/* Action buttons — context-sensitive per status */}
                  <div className="flex gap-1 flex-shrink-0">
                    {capture.review_status === 'pending' && capture.status !== 'archived' && (
                      <>
                        <button
                          onClick={e => { e.stopPropagation(); doAction(capture, 'confirm'); }}
                          disabled={isProcessing}
                          className={`p-1.5 rounded disabled:opacity-50 ${
                            isRefusal && !hasEmbeddedData
                              ? 'hover:bg-orange-100 text-orange-400'
                              : 'hover:bg-green-100 text-green-600'
                          }`}
                          title={isRefusal ? 'Confirm (refusal-shaped — review first)' : 'Confirm (a)'}
                          tabIndex={-1}
                        >
                          <HiCheck className="h-4 w-4" />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); startEdit(capture); }}
                          disabled={isProcessing}
                          className="p-1.5 rounded hover:bg-blue-100 text-blue-600 disabled:opacity-50"
                          title="Edit then confirm (e)"
                          tabIndex={-1}
                        >
                          <HiPencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); doAction(capture, 'discard'); }}
                          disabled={isProcessing}
                          className="p-1.5 rounded hover:bg-red-100 text-red-600 disabled:opacity-50"
                          title="Discard (r)"
                          tabIndex={-1}
                        >
                          <HiTrash className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {capture.review_status === 'discarded' && capture.status !== 'archived' && (
                      <>
                        <button
                          onClick={e => { e.stopPropagation(); doAction(capture, 'restore'); }}
                          disabled={isProcessing}
                          className="p-1.5 rounded hover:bg-blue-100 text-blue-600 disabled:opacity-50"
                          title="Restore to triage"
                          tabIndex={-1}
                        >
                          <HiReply className="h-4 w-4" />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); doAction(capture, 'archive'); }}
                          disabled={isProcessing}
                          className="p-1.5 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-50"
                          title="Archive"
                          tabIndex={-1}
                        >
                          <HiArchive className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {capture.status === 'archived' && (
                      <button
                        onClick={e => { e.stopPropagation(); doAction(capture, 'restore'); }}
                        disabled={isProcessing}
                        className="p-1.5 rounded hover:bg-blue-100 text-blue-600 disabled:opacity-50"
                        title="Restore to triage"
                        tabIndex={-1}
                      >
                        <HiReply className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {isProcessing && (
                  <div className="mt-2 ml-0 flex items-center gap-2 text-xs text-gray-500">
                    <Spinner size="xs" /> Processing...
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CaptureReview;
